/**
 * アクセスモード判定ユーティリティ
 *
 * `VITE_WEB_HOSTNAMES` env（カンマ区切りのホスト名一覧）と `window.location.hostname` を
 * 比較して、現在のアクセスが「WEBアクセス」か「LINE/LIFFアクセス」かを判定する。
 *
 * - WEBホストに一致 → WEB（メール+パスワードログインを想定）
 * - それ以外（localhost等） → LINE/LIFF
 */

export type AccessMode = 'web' | 'line';

const WEB_HOSTNAMES: string[] = (import.meta.env.VITE_WEB_HOSTNAMES ?? '')
  .split(',')
  .map((h: string) => h.trim().toLowerCase())
  .filter((h: string) => h.length > 0);

/**
 * LINE アプリ内蔵ブラウザ（LIFF Browser）かどうか。
 * UA に `Line/X.Y.Z` が含まれるかで判定。iOS/Android 共通。
 */
function isLineInAppBrowser(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /Line\/[\d.]+/i.test(navigator.userAgent);
}

/**
 * LIFF からのリダイレクト由来のアクセスかどうか。
 * URL に `liff.state` クエリがあれば LIFF（LINE 外部ブラウザ + OAuth 経由）。
 */
function hasLiffStateQuery(): boolean {
  if (typeof window === 'undefined') return false;
  return window.location.search.includes('liff.state');
}

/**
 * 現在のホスト名がWEBアクセス用ドメインに一致するか。
 *
 * 注意: LIFF エンドポイントURL と WEB 公開URL が同一ドメイン（例: stg.10flow.ai）の環境では、
 * ホスト名だけでは LIFF/WEB を区別できない。そのため:
 *   - LINE アプリ内蔵ブラウザ（UA に `Line/`）→ 必ず LIFF
 *   - URL に `liff.state` クエリあり → 必ず LIFF
 * これらに該当する場合は WEB ドメインに一致していても WEB 扱いしない。
 */
export function isWebAccess(): boolean {
  if (typeof window === 'undefined') return false;
  if (isLineInAppBrowser()) return false;
  if (hasLiffStateQuery()) return false;
  const hostname = window.location.hostname.toLowerCase();
  return WEB_HOSTNAMES.includes(hostname);
}

/**
 * 現在のアクセスモードを返す。
 */
export function getAccessMode(): AccessMode {
  return isWebAccess() ? 'web' : 'line';
}

/**
 * env に設定されている WEB ホスト名一覧を返す（デバッグ表示用）。
 */
export function getConfiguredWebHostnames(): string[] {
  return [...WEB_HOSTNAMES];
}
